import { withRouter } from 'react-router';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IconName } from '@repo/ui/types/Icon';
import Icon from '@repo/ui/components/ui/Icon';
import Link from '@repo/ui/components/buttons/Link';

export type SubMenuItem = {
  icon: IconName;
  id?: string;
  items?: SubMenuItem[];
  onClick?: (event: React.MouseEvent) => void;
  route?: string;
  shortTitle?: string;
  title: string;
};

export type SubMenuProps = {
  activeRoutes?: boolean;
  icon: IconName;
  items: SubMenuItem[];
  router: { isActive: (route: string) => boolean };
  title: string;
};

export function SubMenu({ items: _items, activeRoutes, title, icon, router }: SubMenuProps) {
  const container = useRef<HTMLLIElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = (event) => {
    event.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (['Enter', 'Space', ' '].includes(event.key)) {
      toggleDropdown(event);
    }
  };

  const handleClickOutside = useCallback(
    (event) => {
      const node = container?.current;
      if (!node?.contains(event.target)) {
        if (isOpen) setIsOpen(false);
      }
    },
    [isOpen],
  );

  const items = _items.map((item, idx) => {
    const isActive = !!item.route && router.isActive(item.route);
    const value = (
      <>
        <Icon name={item.icon} inverse={isActive} />
        {item.title}
      </>
    );

    const action = (e) => {
      handleClickOutside(e);
      item.onClick?.(e);
    };

    return (
      <li
        key={idx}
        className={classNames('not-rounded', { active: isActive })}
        style={{ position: 'relative' }}
      >
        <Link
          to={item.route}
          onClick={action}
          onKeyDown={(e) =>
            e.key === ' ' || e.key === 'Space' || e.key === 'Enter' ? action(e) : false
          }
        >
          {value}
        </Link>
      </li>
    );
  });

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const dropdownMenu = (
    <div className="dropdown-menu notification-menu" id="menu-dropdown">
      <ul className="fa-ul dropdown-menu-items">{items}</ul>
    </div>
  );

  return (
    <li
      ref={container}
      className={classNames('btn-group clickable sub-menu', {
        open: isOpen,
        active: activeRoutes,
      })}
      tabIndex={-1}
      aria-label={title}
      aria-controls="menu-dropdown"
      aria-expanded={isOpen}
    >
      <a
        href=""
        role="button"
        tabIndex={0}
        onClick={(e) => toggleDropdown(e)}
        onKeyDown={handleKeyDown}
      >
        <Icon name={icon} /> {title}
        <i className="fa fa-caret-down ml-md" />
      </a>
      {dropdownMenu}
    </li>
  );
}

export default withRouter(SubMenu);
